import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, MultiChoice, NumberInputWithOptions} from 'platform/components';
import {Grid, Show} from 'platform/foundation';

import {useSelector} from 'react-redux';

import {isNotEmpty, isNotNil} from 'ramda';

import {
  selectTenant,
  useGetAuctionServerListQuery,
  useSourcingGetSourcingServersQuery,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {AddressSearch, useAddress} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {ALLOWED_SELLER_TYPE_KEYS} from '../../../../../../constants/allowedSellerTypeKeys';
import {sourcingFilterConstants} from '../../../../../../constants/sourcingFilterConstants';
import {UNSUPPORTED_ORIGIN_COUNTRY_CODES} from '../../../../../../constants/unsupportedOriginCountryCodes';
import {useApiEnums} from '../../../../../../hooks/useApiEnums';
import {SourcingType, useSourcingType} from '../../../../../../hooks/useSourcingType';
import {AuctionTypeFilter} from '../../../../../../types/AuctionTypeFilter';
import {getAuctionTypePresentation} from '../../../../../../utils/getAuctionTypePresentation';
import {CarStatusFilter} from '../../../../types/CarStatusFilter';
import {useFilters} from '../../../FiltersContext/FiltersContext';
import {allowedCountryCodes} from '../../constants/AllowedCountryCodes';
import {SourcingSlider} from '../SourcingSlider/SourcingSlider';

const carStatusOptions = [
  {value: CarStatusFilter.UPCOMING, label: i18n.t('entity.auction.labels.upcoming')},
  {value: CarStatusFilter.ACTIVE, label: i18n.t('entity.auction.labels.active')},
  {value: CarStatusFilter.ENDED, label: i18n.t('entity.auction.labels.ended')},
];

export function FilterSeller(props: TestIdProps) {
  const auctionTypeOptions = Object.values(AuctionTypeFilter).map((value) => ({
    value,
    label: getAuctionTypePresentation(value)?.label ?? value,
  }));
  const sourcingType = useSourcingType();
  const {filters, onUpdateFilters} = useFilters();
  const {getEnum, isLoading} = useApiEnums();
  const {composeAddress} = useAddress();
  const {data: {country: countryCode} = {}} = useSelector(selectTenant);
  const {data: sellerServers} = useSourcingGetSourcingServersQuery(
    isNotNil(countryCode) ? {countryCode} : skipToken
  );
  const sellerServerOptions =
    sellerServers?.map((sellerServer) => ({
      value: sellerServer.code,
      label: sellerServer.name,
    })) ?? [];

  const {data: auctionServers} = useGetAuctionServerListQuery(undefined, {
    skip: sourcingType !== SourcingType.AUCTIONS,
  });
  const auctionServerOptions =
    auctionServers
      ?.slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((auctionServer) => ({
        value: auctionServer.id,
        label: auctionServer.name,
      })) ?? [];

  const countries = getEnum('country')
    .filter((country) => allowedCountryCodes.includes(country.key))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Wider than the seller countries (imports from the US, Korea…). Countries Alpha does not know
  // are left out, they would fail the whole classifieds request.
  const originCountries = getEnum('country')
    .filter((country) => !UNSUPPORTED_ORIGIN_COUNTRY_CODES.includes(country.key))
    .sort((a, b) => a.label.localeCompare(b.label));

  const sellerTypes = getEnum('seller_type').filter((sellerType) =>
    ALLOWED_SELLER_TYPE_KEYS.includes(sellerType.key)
  );

  const address =
    isNotNil(filters.address) && isNotNil(filters.radius)
      ? {
          street: filters.address?.street ?? '',
          descriptiveNumber: filters.address?.descriptiveNumber ?? null,
          orientationNumber: filters.address?.orientationNumber ?? null,
          city: filters.address?.city ?? '',
          zip: filters.address?.zipCode ?? '',
          addressComplement: null,
          country: filters.address?.country ?? '',
          prefix: filters.address?.prefix ?? null,
          district: null,
          state: filters.address?.state ?? null,
        }
      : null;

  return (
    <DataStatus isLoading={isLoading}>
      <Grid columns={[1, 2, 4]} spacing={8} data-testid="block-filters-seller">
        <MultiChoice
          label={i18n.t('page.filters.labels.sellerCountry')}
          placeholder={i18n.t('general.labels.chooseOptions')}
          value={filters.sellerCountry ?? null}
          options={countries}
          onChange={(value) => onUpdateFilters('sellerCountry', value ?? undefined)}
          data-testid={suffixTestId('sellerCountry', props)}
        />

        <Show
          when={sourcingType === SourcingType.CLASSIFIEDS}
          whenFeatureEnabled={featureFlags.SOURCING_ORIGIN_COUNTRY_FILTER}
        >
          <MultiChoice
            label={i18n.t('entity.vehicle.labels.countryOfOrigin')}
            tooltip={i18n.t('entity.sourcing.filters.labels.originCountryTooltip')}
            placeholder={i18n.t('general.labels.chooseOptions')}
            value={filters.originCountry ?? null}
            options={originCountries}
            onChange={(value) =>
              onUpdateFilters(
                'originCountry',
                isNotNil(value) && isNotEmpty(value) ? value : undefined
              )
            }
            data-testid={suffixTestId('originCountry', props)}
          />
        </Show>

        <Show when={sourcingType === SourcingType.CLASSIFIEDS}>
          <MultiChoice
            label={i18n.t('page.filters.labels.sellerServer')}
            placeholder={i18n.t('general.labels.chooseOption')}
            value={filters.sellerServer ?? null}
            options={sellerServerOptions}
            onChange={(value) => onUpdateFilters('sellerServer', value ?? undefined)}
            data-testid={suffixTestId('sellerServer', props)}
          />
        </Show>

        <Show when={sourcingType === SourcingType.CLASSIFIEDS}>
          <MultiChoice
            label={i18n.t('page.filters.labels.sellerType')}
            placeholder={i18n.t('general.labels.chooseOption')}
            options={sellerTypes}
            value={filters.sellerType ?? null}
            onChange={(value) => onUpdateFilters('sellerType', value ?? undefined)}
            data-testid={suffixTestId('sellerType', props)}
          />
        </Show>

        <Show when={sourcingType === SourcingType.AUCTIONS}>
          <MultiChoice
            label={i18n.t('page.filters.labels.auctionType')}
            placeholder={i18n.t('general.labels.chooseOption')}
            options={auctionTypeOptions}
            value={filters.auctionType ?? null}
            onChange={(value) => onUpdateFilters('auctionType', value ?? undefined)}
            data-testid={suffixTestId('auctionType', props)}
          />
        </Show>

        <Show when={sourcingType === SourcingType.AUCTIONS}>
          <MultiChoice
            label={i18n.t('page.filters.labels.carStatus')}
            placeholder={i18n.t('general.labels.chooseOption')}
            options={carStatusOptions}
            value={filters.carStatus ?? null}
            onChange={(value) => onUpdateFilters('carStatus', value ?? undefined)}
            data-testid={suffixTestId('carStatus', props)}
          />
        </Show>

        <Show when={sourcingType === SourcingType.AUCTIONS}>
          <MultiChoice
            label={i18n.t('page.filters.labels.auctionServer')}
            placeholder={i18n.t('general.labels.chooseOption')}
            options={auctionServerOptions}
            value={filters.auctionServer ?? null}
            onChange={(value) => onUpdateFilters('auctionServer', value ?? undefined)}
            data-testid={suffixTestId('auctionServer', props)}
          />
        </Show>

        <Show when={sourcingType === SourcingType.CLASSIFIEDS}>
          <SourcingSlider
            label={i18n.t('entity.warehouse.labels.averageCarsInStock')}
            min={sourcingFilterConstants.AVG_NUMBER_ADS_MIN}
            max={sourcingFilterConstants.AVG_NUMBER_ADS_MAX}
            marks={sourcingFilterConstants.AVG_NUMBER_ADS_MARKS}
            hasMaxPlus
            shouldHideSlider
            value={filters.avgNumberAds}
            onChange={(value) => onUpdateFilters('avgNumberAds', value)}
            data-testid={suffixTestId('avgNumberAds', props)}
          />
        </Show>

        <Show when={sourcingType === SourcingType.CLASSIFIEDS}>
          <AddressSearch
            initialSearchText={isNotNil(address) ? composeAddress(address) : null}
            label={i18n.t('page.filters.labels.location')}
            onSelect={(address) => onUpdateFilters('address', address)}
          />

          <NumberInputWithOptions
            label={`${i18n.t('page.filters.labels.radius')} (km)`}
            placeholder={i18n.t('general.labels.chooseOption')}
            options={[5, 10, 20, 50, 100]}
            decimalPlaces={0}
            value={filters.radius ?? null}
            // min prop is not implemented, so here is a workaround
            onChange={(value) => onUpdateFilters('radius', (value ?? 0) < 0 ? 0 : value)}
            data-testid={suffixTestId('radius', props)}
            isClearable
          />
        </Show>
      </Grid>
    </DataStatus>
  );
}
